import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import intl from "react-intl-universal";
import { TbExternalLink, TbFileExport } from "react-icons/tb";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useToast } from "../../../../Hooks/toast";
import {
  BAAS_API_DOCS_URL,
  BAAS_API_METRICS_SUPPORTED,
  createBaasApiKey,
  createBaasWebhook,
  fetchBaasApiAccessInfo,
  fetchBaasApiKeys,
  fetchBaasApiLogs,
  fetchBaasApiMetrics,
  fetchBaasPixMetrics,
  fetchBaasWebhookDeliveries,
  fetchBaasWebhookEventCatalog,
  fetchBaasWebhooks,
  revealBaasApiKey,
  revokeBaasApiKey,
  testBaasWebhook,
  toggleBaasWebhook,
} from "../../../../Services/baasApiApi";
import { isBaasHmlInstance } from "../../../../Services/baasInstancesApi";
import { isBaasFullConsoleRole } from "../baasCapabilities";
import { BaasApiPageSkeleton } from "../components/BaasSkeleton";
import { BaasApiAccessPanel } from "./components/BaasApiAccessPanel";
import { BaasApiFilterBar } from "./components/BaasApiFilterBar";
import { BaasApiKeyCreateModal } from "./components/BaasApiKeyCreateModal";
import { BaasApiKeyRevealModal } from "./components/BaasApiKeyRevealModal";
import { BaasApiKeysPanel } from "./components/BaasApiKeysPanel";
import { BaasApiLogsPanel } from "./components/BaasApiLogsPanel";
import { BaasApiMonitoringTabs } from "./components/BaasApiMonitoringTabs";
import { BaasApiOverviewPanel } from "./components/BaasApiOverviewPanel";
import { BaasApiPlaceholderTab } from "./components/BaasApiPlaceholderTab";
import { BaasApiQuickActions } from "./components/BaasApiQuickActions";
import { BaasApiSlaPanel } from "./components/BaasApiSlaPanel";
import { BaasWebhookCreateModal } from "./components/BaasWebhookCreateModal";
import { BaasWebhookLogsModal } from "./components/BaasWebhookLogsModal";
import { BaasWebhooksPanel } from "./components/BaasWebhooksPanel";

const downloadLogsCsv = (logs) => {
  const header = ["date", "method", "path", "status_code", "duration_ms", "correlation_id"];
  const rows = logs.map((entry) =>
    [
      entry.date ?? "",
      entry.method ?? "",
      entry.path ?? "",
      entry.statusCode ?? "",
      entry.durationMs ?? "",
      entry.correlationId ?? "",
    ]
      .map((value) => `"${String(value).replace(/"/g, '""')}"`)
      .join(","),
  );
  const blob = new Blob([[header.join(","), ...rows].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `baas-api-logs-${Date.now()}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

export const BaasApiPage = () => {
  const { selectedInstance } = useBaasInstance();
  const { addToast } = useToast();
  const { can, capabilities } = useBaasCapabilities();
  const canManageApi = can("api.keys.manage");
  const isLoading = useBaasPageLoading(selectedInstance?.id);
  const selectedInstanceIdRef = useRef(selectedInstance?.id);
  selectedInstanceIdRef.current = selectedInstance?.id;

  const [filters, setFilters] = useState({ period: "24h" });
  const [activeTab, setActiveTab] = useState("overview");
  const [accessInfo, setAccessInfo] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [keysManageSupported, setKeysManageSupported] = useState(false);
  const [webhooksManageSupported, setWebhooksManageSupported] = useState(false);
  const [metricsData, setMetricsData] = useState(null);
  const [pixMetrics, setPixMetrics] = useState(null);
  const [apiLogs, setApiLogs] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [keyModalOpen, setKeyModalOpen] = useState(false);
  const [webhookModalOpen, setWebhookModalOpen] = useState(false);
  const [creatingKey, setCreatingKey] = useState(false);
  const [creatingWebhook, setCreatingWebhook] = useState(false);
  const [testingWebhookId, setTestingWebhookId] = useState(null);
  const [webhookLogsModalOpen, setWebhookLogsModalOpen] = useState(false);
  const [webhookLogsTarget, setWebhookLogsTarget] = useState(null);
  const [webhookDeliveries, setWebhookDeliveries] = useState([]);
  const [webhookDeliveriesLoading, setWebhookDeliveriesLoading] = useState(false);
  const [webhookDeliveriesError, setWebhookDeliveriesError] = useState(false);
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [revealTarget, setRevealTarget] = useState(null);
  const [revealFullKey, setRevealFullKey] = useState(null);
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealError, setRevealError] = useState(null);

  const canRevealApiKey = isBaasFullConsoleRole(capabilities) && keysManageSupported;

  useEffect(() => {
    setWebhookLogsModalOpen(false);
    setWebhookLogsTarget(null);
    setWebhookDeliveries([]);
    setWebhookDeliveriesError(false);
    setRevealModalOpen(false);
    setRevealTarget(null);
    setRevealFullKey(null);
    setRevealError(null);
  }, [selectedInstance?.id]);

  const notifyManageUnavailable = useCallback(
    (messageKey) => {
      addToast({
        type: "info",
        title: intl.get(messageKey),
      });
    },
    [addToast],
  );

  const notifyActionError = useCallback(
    (fallbackKey, error) => {
      addToast({
        type: "error",
        title: error?.detail || error?.title || intl.get(fallbackKey),
      });
    },
    [addToast],
  );

  // Keys / webhooks / scopes — always scoped to the console-selected instance
  // (same cancel-on-switch pattern as Contas / Usuários).
  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance) {
      setAccessInfo(null);
      setApiKeys([]);
      setWebhooks([]);
      setWebhookEvents([]);
      setKeysManageSupported(false);
      setWebhooksManageSupported(false);
      setDataLoading(false);
      return undefined;
    }

    setDataLoading(true);
    setAccessInfo(null);
    setApiKeys([]);
    setWebhooks([]);
    setWebhookEvents([]);
    setKeysManageSupported(false);
    setWebhooksManageSupported(false);

    (async () => {
      try {
        const settled = await Promise.allSettled([
          fetchBaasApiAccessInfo(selectedInstance),
          fetchBaasApiKeys(selectedInstance),
          fetchBaasWebhooks(selectedInstance),
          fetchBaasWebhookEventCatalog(selectedInstance),
        ]);

        if (cancelled) return;

        const access = settled[0].status === "fulfilled" ? settled[0].value : null;
        const keysResult =
          settled[1].status === "fulfilled"
            ? settled[1].value
            : { items: [], manageSupported: false };
        const webhooksResult =
          settled[2].status === "fulfilled"
            ? settled[2].value
            : { items: [], manageSupported: false };
        const eventsResult =
          settled[3].status === "fulfilled" ? settled[3].value : [];

        setAccessInfo(access);
        setApiKeys(keysResult.items);
        setWebhooks(webhooksResult.items);
        setWebhookEvents(eventsResult);
        setKeysManageSupported(Boolean(keysResult.manageSupported));
        setWebhooksManageSupported(Boolean(webhooksResult.manageSupported));

        if (!access && settled[0].status === "rejected") {
          addToast({
            type: "error",
            title: intl.get("baas.api.errors.loadFailed"),
          });
        }
      } catch {
        if (cancelled) return;
        setAccessInfo(null);
        setApiKeys([]);
        setWebhooks([]);
        setWebhookEvents([]);
        setKeysManageSupported(false);
        setWebhooksManageSupported(false);
        addToast({
          type: "error",
          title: intl.get("baas.api.errors.loadFailed"),
        });
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, addToast]);

  // Metrics / API logs / Pix metrics — same selected instance as Contas.
  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance || !BAAS_API_METRICS_SUPPORTED) {
      setMetricsData(null);
      setPixMetrics(null);
      setApiLogs([]);
      setMetricsLoading(false);
      setLogsLoading(false);
      return undefined;
    }

    const period = filters.period;
    setMetricsLoading(true);
    setLogsLoading(true);
    setMetricsData(null);
    setPixMetrics(null);
    setApiLogs([]);

    (async () => {
      try {
        const [metricsResult, logsResult, pixResult] = await Promise.allSettled([
          fetchBaasApiMetrics(selectedInstance, { period }),
          fetchBaasApiLogs(selectedInstance, { period, page: 1, pageSize: 50 }),
          fetchBaasPixMetrics(selectedInstance, { period }),
        ]);
        if (cancelled) return;
        setMetricsData(metricsResult.status === "fulfilled" ? metricsResult.value : null);
        setApiLogs(logsResult.status === "fulfilled" ? logsResult.value.items : []);
        setPixMetrics(pixResult.status === "fulfilled" ? pixResult.value : null);
      } finally {
        if (!cancelled) {
          setMetricsLoading(false);
          setLogsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, filters.period]);

  const openKeyModal = useCallback(() => {
    setActiveTab("api-keys");
    if (!keysManageSupported || !canManageApi) {
      notifyManageUnavailable("baas.api.unavailable.keysManage");
      return;
    }
    setKeyModalOpen(true);
  }, [keysManageSupported, canManageApi, notifyManageUnavailable]);

  const openWebhookModal = useCallback(() => {
    setActiveTab("webhook");
    if (!webhooksManageSupported || !canManageApi) {
      notifyManageUnavailable("baas.api.unavailable.webhooksManage");
      return;
    }
    setWebhookModalOpen(true);
  }, [webhooksManageSupported, canManageApi, notifyManageUnavailable]);

  const handleCreateKey = async (payload) => {
    setCreatingKey(true);
    try {
      const created = await createBaasApiKey(selectedInstance, payload);
      const keysResult = await fetchBaasApiKeys(selectedInstance);
      setApiKeys(keysResult.items);
      setKeysManageSupported(Boolean(keysResult.manageSupported));
      addToast({
        type: "success",
        title: intl.get("baas.api.keys.createSuccess"),
      });
      return created;
    } catch (error) {
      if (!keysManageSupported || error?.code === "not_supported" || error?.status === 404) {
        notifyManageUnavailable("baas.api.unavailable.keysManage");
      } else {
        const fieldDetail = (() => {
          const errors = error?.errors;
          if (!errors || typeof errors !== "object") return null;
          const first = Object.values(errors).flat().find(Boolean);
          return first ? String(first) : null;
        })();
        addToast({
          type: "error",
          title:
            fieldDetail
            || error?.detail
            || error?.title
            || intl.get("baas.api.errors.loadFailed"),
        });
      }
      return null;
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      await revokeBaasApiKey(selectedInstance, keyId);
      const keysResult = await fetchBaasApiKeys(selectedInstance);
      setApiKeys(keysResult.items);
      setKeysManageSupported(Boolean(keysResult.manageSupported));
      addToast({
        type: "success",
        title: intl.get("baas.api.keys.revokeSuccess"),
      });
    } catch (error) {
      if (!keysManageSupported || error?.code === "not_supported" || error?.status === 404) {
        notifyManageUnavailable("baas.api.unavailable.keysManage");
      } else {
        notifyActionError("baas.api.errors.loadFailed", error);
      }
    }
  };

  const closeRevealModal = useCallback(() => {
    setRevealModalOpen(false);
    setRevealTarget(null);
    setRevealFullKey(null);
    setRevealError(null);
    setRevealLoading(false);
  }, []);

  const handleRevealKey = async (item) => {
    if (!canRevealApiKey || !item?.id) return;
    setRevealTarget(item);
    setRevealFullKey(null);
    setRevealError(null);
    setRevealModalOpen(true);
    setRevealLoading(true);
    try {
      const revealed = await revealBaasApiKey(selectedInstance, item.id);
      if (!revealed?.fullKey) {
        setRevealError(intl.get("baas.api.keys.revealError"));
        return;
      }
      setRevealFullKey(revealed.fullKey);
    } catch (error) {
      setRevealError(
        error?.detail || error?.title || intl.get("baas.api.keys.revealError"),
      );
    } finally {
      setRevealLoading(false);
    }
  };

  const handleCreateWebhook = async (payload) => {
    setCreatingWebhook(true);
    try {
      await createBaasWebhook(selectedInstance, payload);
      const webhooksResult = await fetchBaasWebhooks(selectedInstance);
      setWebhooks(webhooksResult.items);
      setWebhooksManageSupported(Boolean(webhooksResult.manageSupported));
      addToast({
        type: "success",
        title: intl.get("baas.api.webhooks.createSuccess"),
      });
      return true;
    } catch (error) {
      if (
        !webhooksManageSupported ||
        error?.code === "not_supported" ||
        error?.status === 404
      ) {
        notifyManageUnavailable("baas.api.unavailable.webhooksManage");
      } else {
        notifyActionError("baas.api.errors.loadFailed", error);
      }
      return false;
    } finally {
      setCreatingWebhook(false);
    }
  };

  const handleToggleWebhook = async (webhookId, active) => {
    try {
      await toggleBaasWebhook(selectedInstance, webhookId, active);
      const webhooksResult = await fetchBaasWebhooks(selectedInstance);
      setWebhooks(webhooksResult.items);
      setWebhooksManageSupported(Boolean(webhooksResult.manageSupported));
    } catch (error) {
      if (
        !webhooksManageSupported ||
        error?.code === "not_supported" ||
        error?.status === 404
      ) {
        notifyManageUnavailable("baas.api.unavailable.webhooksManage");
      } else {
        notifyActionError("baas.api.errors.loadFailed", error);
      }
    }
  };

  const handleTestWebhook = async (webhookId) => {
    setTestingWebhookId(webhookId);
    try {
      const result = await testBaasWebhook(selectedInstance, webhookId);
      addToast({
        type: "success",
        title: intl.get("baas.api.webhooks.testSuccess", {
          status: result.statusCode,
          latency: result.latencyMs,
        }),
      });
    } catch (error) {
      if (
        !webhooksManageSupported ||
        error?.code === "not_supported" ||
        error?.status === 404
      ) {
        notifyManageUnavailable("baas.api.unavailable.webhooksManage");
      } else {
        notifyActionError("baas.api.errors.loadFailed", error);
      }
    } finally {
      setTestingWebhookId(null);
    }
  };

  const handleViewWebhookLogs = async (webhook) => {
    const instance = selectedInstance;
    if (!instance) return;

    setWebhookLogsTarget(webhook);
    setWebhookLogsModalOpen(true);
    setWebhookDeliveries([]);
    setWebhookDeliveriesError(false);
    setWebhookDeliveriesLoading(true);
    try {
      const result = await fetchBaasWebhookDeliveries(instance, webhook.id, {
        page: 1,
        pageSize: 50,
      });
      if (selectedInstanceIdRef.current !== instance.id) return;
      setWebhookDeliveries(result.items);
      setWebhookDeliveriesError(Boolean(result.error));
    } catch {
      if (selectedInstanceIdRef.current !== instance.id) return;
      setWebhookDeliveries([]);
      setWebhookDeliveriesError(true);
    } finally {
      if (selectedInstanceIdRef.current === instance.id) {
        setWebhookDeliveriesLoading(false);
      }
    }
  };

  const closeWebhookLogsModal = () => {
    setWebhookLogsModalOpen(false);
    setWebhookLogsTarget(null);
    setWebhookDeliveries([]);
    setWebhookDeliveriesError(false);
  };

  const handleExportLogs = () => {
    if (!apiLogs.length) {
      addToast({
        type: "info",
        title: intl.get("baas.api.toast.exportLogsEmpty"),
      });
      setActiveTab("logs");
      return;
    }
    downloadLogsCsv(apiLogs);
    addToast({
      type: "success",
      title: intl.get("baas.api.toast.exportLogsDone"),
    });
  };

  const keysEmptyMessage = useMemo(
    () =>
      keysManageSupported
        ? intl.get("baas.api.keys.empty")
        : intl.get("baas.api.keys.emptyBackend"),
    [keysManageSupported],
  );

  const webhooksEmptyMessage = useMemo(
    () =>
      webhooksManageSupported
        ? intl.get("baas.api.webhooks.empty")
        : intl.get("baas.api.webhooks.emptyBackend"),
    [webhooksManageSupported],
  );

  const healthStatus = useMemo(() => {
    if (!BAAS_API_METRICS_SUPPORTED || metricsLoading) return "unknown";
    return metricsData?.health_status ?? metricsData?.healthStatus ?? "healthy";
  }, [metricsData, metricsLoading]);

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return BAAS_API_METRICS_SUPPORTED ? (
          <BaasApiOverviewPanel
            filters={filters}
            apiKeys={apiKeys}
            webhooks={webhooks}
            metricsData={metricsData}
            metricsLoading={metricsLoading}
            pixMetrics={pixMetrics}
          />
        ) : (
          <BaasApiPlaceholderTab
            titleKey="baas.api.unavailable.monitoringTitle"
            descriptionKey="baas.api.unavailable.monitoringDescription"
          />
        );
      case "logs":
        return BAAS_API_METRICS_SUPPORTED ? (
          <BaasApiLogsPanel logs={apiLogs} loading={logsLoading} />
        ) : (
          <BaasApiPlaceholderTab
            titleKey="baas.api.tabs.logs"
            descriptionKey="baas.api.placeholders.logs"
          />
        );
      case "sla":
        return BAAS_API_METRICS_SUPPORTED ? (
          <BaasApiSlaPanel
            filters={filters}
            metricsData={metricsData}
            metricsLoading={metricsLoading}
          />
        ) : (
          <BaasApiPlaceholderTab
            titleKey="baas.api.tabs.sla"
            descriptionKey="baas.api.placeholders.sla"
          />
        );
      case "webhook":
        return (
          <BaasWebhooksPanel
            webhooks={webhooks}
            onRegister={openWebhookModal}
            onToggle={handleToggleWebhook}
            onTest={handleTestWebhook}
            onViewLogs={handleViewWebhookLogs}
            testingId={testingWebhookId}
            manageDisabled={!webhooksManageSupported || !canManageApi}
            emptyMessage={webhooksEmptyMessage}
          />
        );
      case "api-keys":
      default:
        return (
          <BaasApiKeysPanel
            apiKeys={apiKeys}
            onGenerate={openKeyModal}
            onRevoke={handleRevokeKey}
            onReveal={handleRevealKey}
            manageDisabled={!keysManageSupported || !canManageApi}
            revealEnabled={canRevealApiKey}
            emptyMessage={keysEmptyMessage}
            sectionSubtitle={
              isBaasHmlInstance(selectedInstance)
                ? intl.get("baas.api.keys.sectionSubtitleHml")
                : intl.get("baas.api.keys.sectionSubtitleProd")
            }
          />
        );
    }
  };

  if (isLoading || dataLoading) {
    return <BaasApiPageSkeleton />;
  }

  return (
    <div className="baas-api min-w-0 space-y-5">
      <header className="min-w-0 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.api.monitoringSection")}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.api.monitoringSubtitle")}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {BAAS_API_DOCS_URL ? (
            <a
              href={BAAS_API_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary shrink-0"
            >
              <TbExternalLink className="h-4 w-4" />
              {intl.get("baas.api.actions.openDocs")}
            </a>
          ) : (
            <button
              type="button"
              className="btn-primary shrink-0"
              onClick={handleExportLogs}
            >
              <TbFileExport className="h-4 w-4" />
              {intl.get("baas.api.actions.export")}
            </button>
          )}
        </div>
      </header>

      {accessInfo ? (
        <BaasApiAccessPanel accessInfo={accessInfo} instance={selectedInstance} />
      ) : null}

      <BaasApiFilterBar
        filters={filters}
        onFiltersChange={setFilters}
        healthStatus={healthStatus}
      />

      <BaasApiQuickActions
        onGenerateKey={openKeyModal}
        onCreateWebhook={openWebhookModal}
        keysEnabled={canManageApi && keysManageSupported}
        webhooksEnabled={canManageApi && webhooksManageSupported}
      />

      <div className="min-w-0 space-y-4">
        <BaasApiMonitoringTabs activeTab={activeTab} onTabChange={setActiveTab} />
        {renderTabContent()}
      </div>

      <BaasApiKeyCreateModal
        open={keyModalOpen}
        onClose={() => setKeyModalOpen(false)}
        onSubmit={handleCreateKey}
        submitting={creatingKey}
        instance={selectedInstance}
      />

      <BaasApiKeyRevealModal
        open={revealModalOpen}
        onClose={closeRevealModal}
        fullKey={revealFullKey}
        keyName={revealTarget?.name || ""}
        loading={revealLoading}
        error={revealError}
      />

      <BaasWebhookCreateModal
        open={webhookModalOpen}
        onClose={() => setWebhookModalOpen(false)}
        onSubmit={handleCreateWebhook}
        submitting={creatingWebhook}
        eventCatalog={webhookEvents}
      />

      <BaasWebhookLogsModal
        open={webhookLogsModalOpen}
        onClose={closeWebhookLogsModal}
        webhook={webhookLogsTarget}
        deliveries={webhookDeliveries}
        loading={webhookDeliveriesLoading}
        loadError={webhookDeliveriesError}
      />
    </div>
  );
};
